import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

/**
 * Service providing platform detection (browser vs server) for runtime environment checks.
 *
 * @remarks
 * Useful for handling browser-specific APIs or SSR scenarios in Angular Universal.
 *
 * @example
 * ```
 * export class AppComponent {
 *   private platformContext = inject(PlatformContextData);
 *
 *   ngOnInit(): void {
 *     if (this.platformContext.isBrowser()) {
 *       // Browser-only code (e.g., localStorage, window)
 *       localStorage.setItem('key', 'value');
 *     }
 *
 *     if (this.platformContext.isServer()) {
 *       // Server-only code (e.g., SSR optimizations)
 *       console.log('Rendering on server');
 *     }
 *   }
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class PlatformContextData {
  private readonly platformId = inject<string>(PLATFORM_ID);

  /** Returns true if running in browser environment. */
  isBrowser = (): boolean => isPlatformBrowser(this.platformId);

  /** Returns true if running in server environment (SSR). */
  isServer = (): boolean => isPlatformServer(this.platformId);
}
